import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import pluralize from 'pluralize';
import { FC, useCallback } from 'react';
import AppSpinner from '../../common/components/AppSpinner';
import SimpleTitlePanel from '../../common/components/SimpleTitlePanel';
import { resolveRepoIcon } from 'common-icons';
import StatButton from '../../elements/StatButton/StatButton';
import { loadIrrelevantApps } from '../actions/irrelevant-apps-actions';
import { filtersExists } from '../actions/reason-filters-actions';
import { ReasonFilter, ReasonFilterType } from '../stores/reason-filters-store';
import type { Property } from 'csstype';
import { createNewDateFromNumber } from '../../elements/Aggregation/aggregation-utils';
import { setRelevetAppModal } from '../store-actions/irrelevant-apps-store-actions';
import MakeRelevant from '../irrelevant-app-options/MakeRelevant';
import { IrrelevantAppsTableProps } from '../irrelevant-apps-types';

const IrrelevantAppsTable: FC<IrrelevantAppsTableProps> = ({
  irrelevantApps,
  filters,
  isLoadingFilters,
}) => {
  const { classes } = useStyles();

  const handleFilterChange = useCallback((filter: string) => {
    loadIrrelevantApps(filter as ReasonFilterType);
  }, []);

  return (
    <div className={classes.irrelevantApps}>
      <SimpleTitlePanel
        text={`${pluralize('IRRELEVANT APP', irrelevantApps.length)} (${
          irrelevantApps.length
        })`}>
        <div className={classes.filtersContainer}>
          {filters.map(filter => (
            <span key={filter.reason}>
              <StatButton
                className={classes.statButton}
                count={filter.count}
                label={filter.reason}
                disabled={filter.count === 0}
                delta={0}
                trend='neutral'
                active={filtersExists(ReasonFilter[filter.reason])}
                onClick={() => handleFilterChange(ReasonFilter[filter.reason])}
              />
            </span>
          ))}
        </div>
      </SimpleTitlePanel>

      {isLoadingFilters ? (
        <AppSpinner text='Fetching Irrelevant Apps' />
      ) : (
        <div className={classes.irrelevantAppsTable}>
          <TableContainer component={Paper} sx={{ width: '100%' }}>
            <Table aria-label='Irrelevant apps table'>
              <TableHead className={classes.tableHead}>
                <TableRow className={classes.tableRow}>
                  <TableCell>
                    <Typography variant='body1' color='text.primary'>
                      Not Relevant Apps ({irrelevantApps.length})
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableHead className={classes.tableHead}>
                <TableRow className={classes.tableRow}>
                  <TableCell className={classes.tableCell}>App Name</TableCell>
                  <TableCell className={classes.tableCell}>Reason</TableCell>
                  <TableCell className={classes.tableCell}>Info</TableCell>
                  <TableCell className={classes.tableCell}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {irrelevantApps.map(row => {
                  const RepoIcon = resolveRepoIcon(row.type, row.fakeApp);
                  return (
                    <TableRow className={classes.tableRow} key={row.appId}>
                      <TableCell sx={{ width: 400, maxHeight: 50 }}>
                        <div className={classes.repoIconStack}>
                          {RepoIcon && (
                            <RepoIcon className={classes.repoIcon} />
                          )}
                          <Typography variant='body2' color='text.primary'>
                            {row.name}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        {row.reasons.map((reason, index) => (
                          <Typography
                            key={index}
                            variant='body2'
                            color='text.primary'>
                            {reason}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2' color='text.primary'>
                          {row.lastCodeChange
                            ? `Last used date: ${createNewDateFromNumber(
                                row.lastCodeChange,
                              )?.toLocaleDateString()}`
                            : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <MakeRelevant
                          setShowDialog={setRelevetAppModal}
                          irrelevantApp={row}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles<void, 'inventory'>()(
  (theme: Theme, _params, classes) => ({
    irrelevantAppsTable: {
      marginBlockStart: theme.spacing(2),
    },
    tableHead: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tableCell: {
      border: 'none !important',
      textTransform: 'uppercase',
      color: `${theme.palette.text.secondary} !important`,
      fontSize: `${theme.typography.body2.text} !important`,
    },
    irrelevantApps: {
      padding: theme.spacing(2),
      height: '100%',
      overflowY: 'auto',
    },
    tableRow: {
      '& > td, th': {
        padding: '12px 50px',
      },
      '&:last-child td, &:last-child th': { border: 0 },
    },
    reason: {
      margin: '0',
      '& + &': {
        marginBlockStart: theme.spacing(2),
      },
    },
    inventory: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingBlock: theme.spacing(1),
      paddingInline: theme.spacing(2),
      gap: theme.spacing(1),
    },
    textContainer: {
      whiteSpace: 'nowrap',
      paddingInlineEnd: theme.spacing(2),
    },
    panelContainer: {
      paddingInline: theme.spacing(1),
      paddingBlock: theme.spacing(1),
    },
    filtersContainer: {
      display: 'inline-flex',
      gap: theme.spacing(1.5),
      flexWrap: 'wrap',
    },
    [theme.breakpoints.up('md')]: {
      [`& .${classes.inventory}`]: {
        flexDirection: 'row' as Property.FlexDirection,
        alignItems: 'center',
      },
    },
    button: {
      padding: '0px !important',
      lineHeight: 'normal !important',
      textDecoration: 'none',
    },
    repoIconStack: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
      alignItems: 'center',
    },
    repoIcon: {
      width: '0.8rem',
      height: '0.8rem',
      flexShrink: 0,
    },
  }),
);

export default IrrelevantAppsTable;
